import { type Data } from '../common/types'

export type ControlMessage<Payload = Data> = {
  [key: string]: Data
} & {
  type: string
  payload?: Payload
}

export type ControlInstanceKey = {
  elementKey: string
  propPath: string
}

export type ControlInstanceArgs<M extends ControlMessage = ControlMessage> = {
  instanceKey: ControlInstanceKey
  sendMessage: SendMessage<M>
}

export type SendMessage<M extends ControlMessage = ControlMessage> = (
  message: M,
) => void

export abstract class ControlInstance<
  M extends ControlMessage = ControlMessage,
> {
  public readonly instanceKey: ControlInstanceKey
  protected readonly sendMessage: SendMessage<M>

  constructor({ instanceKey, sendMessage }: ControlInstanceArgs<M>) {
    this.instanceKey = instanceKey
    this.sendMessage = sendMessage
  }

  get elementKey() {
    return this.instanceKey.elementKey
  }

  get propPath() {
    return this.instanceKey.propPath
  }

  abstract recv(message: M): void

  /**
   * Subscribe to changes to the instance state; returns an unsubscribe function.
   */
  abstract subscribe(listener: () => void): () => void

  /**
   * Returns true if the control represents a composite prop value (a prop that contains other props).
   *
   * In contrast with `children()`, this return value doesn't change over the control lifetime.
   */
  abstract isCompositeProp(): boolean

  /**
   * Returns a list of child control instances for this control's immediate nested props.
   */
  abstract children(): ControlInstance[]

  /**
   * Returns the control instance for the nested prop identified by `key`.
   *
   * For list controls, `key` is a stringified item index.
   */
  abstract child(key: string): ControlInstance | undefined

  /**
   * Returns true if the control resolves to a renderable node.
   *
   * In React, that means the resolved value is a `ReactNode`. In other renderers, it is the equivalent
   * renderable node type.
   */
  abstract resolvesToRenderableNode(): boolean
}

export class DefaultControlInstance extends ControlInstance {
  recv(_message: ControlMessage) {
    // Do nothing
  }

  subscribe(_listener: () => void): () => void {
    // No mutable state, nothing to subscribe to
    return () => {}
  }

  isCompositeProp(): boolean {
    return false
  }

  children(): ControlInstance[] {
    return []
  }

  child(_key: string) {
    return undefined
  }

  resolvesToRenderableNode(): boolean {
    return false
  }
}

export type SendMessageType<I> = I extends ControlInstance<infer M> ? M : never
